import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { BlandWebClient as VoiceCallWebClient } from 'bland-client-js-sdk';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import ReCAPTCHA from "react-google-recaptcha";
import { LoadingSpinnerRing } from './AppLoadingState';

/**
 * ChatWidget component for handling voice calls with the embedded voice SDK
 * 
 * IMPORTANT: Browser Deprecation Warning Handling
 * ----------------------------------------------
 * This component uses the vendor JavaScript SDK which relies on ScriptProcessorNode,
 * a deprecated Web Audio API feature. Modern browsers show a deprecation warning:
 * "[Deprecation] The ScriptProcessorNode is deprecated. Use AudioWorkletNode instead."
 * 
 * We've implemented two workarounds:
 * 1. Console warning filter - To suppress the browser warning that we can't fix directly
 * 2. AudioContext options - Setting latencyHint and matching sampleRate to inform browsers
 *    that we're aware of the deprecation but need to use it for compatibility
 * 
 * These workarounds should be reviewed once the SDK moves to AudioWorkletNode.
 * More info: https://developer.mozilla.org/en-US/docs/Web/API/AudioWorkletNode
 */

// reCAPTCHA site key 
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

// Check if VoiceCallWebClient exists
// console.log('VoiceCallWebClient available:', typeof VoiceCallWebClient !== 'undefined');
// Log the constructor properties to check API
// console.log('VoiceCallWebClient constructor:', VoiceCallWebClient.toString().substring(0, 100));

const ChatWidget = ({ 
  agentId,
  name = 'Mr. Dial',
  avatar = 'MD',
  status = 'Click to speak with me!',
  onCallStart,
  onCallEnd,
  onError,
  isAuthenticated = false,
  className = '',
  compact = false
}) => {
  const [isCallActive, setIsCallActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [client, setClient] = useState(null);
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [throttled, setThrottled] = useState(false);
  const [throttleTimeRemaining, setThrottleTimeRemaining] = useState(0);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const recaptchaRef = useRef(null);
  
  // Use useRef for function storage to break circular dependencies
  const functionRefs = useRef({});

  // Load reCAPTCHA script
  useEffect(() => {
    // console.log('Checking for reCAPTCHA script...');
    // Check if script is already loaded
    if (window.grecaptcha) {
      // console.log('reCAPTCHA already loaded in window object');
      setRecaptchaLoaded(true);
      return;
    }

    // Check if script tag is already in the document
    const existingScript = document.querySelector('script[src*="recaptcha/api.js"]');
    if (existingScript) {
      // console.log('reCAPTCHA script tag already exists in document');
      setRecaptchaLoaded(true);
      return;
    }

    // console.log('Loading reCAPTCHA script...');
    // Create script element
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=explicit`;
    script.async = true;
    script.defer = true;
    script.id = 'recaptcha-script';
    
    script.onload = () => {
      // console.log('reCAPTCHA script loaded successfully');
      setRecaptchaLoaded(true);
    };
    
    script.onerror = (error) => {
      // console.error('Error loading reCAPTCHA script:', error);
    };
    
    // Add script to document
    document.head.appendChild(script);
    
    // As a fallback, set loaded to true after 3 seconds
    // This helps in case the onload event doesn't fire properly
    const fallbackTimer = setTimeout(() => {
      if (!recaptchaLoaded && window.grecaptcha) {
        // console.log('reCAPTCHA loaded via fallback timer check');
        setRecaptchaLoaded(true);
      }
    }, 3000);
    
    return () => clearTimeout(fallbackTimer);
  }, [recaptchaLoaded]);

  // Additional check to force recaptchaLoaded if window.grecaptcha exists
  useEffect(() => {
    if (!recaptchaLoaded && window.grecaptcha) {
      // console.log('Setting recaptchaLoaded to true based on window.grecaptcha check');
      setRecaptchaLoaded(true);
    }
    
    // Check if captchaRequired is true but the captcha isn't showing
    if (captchaRequired && !captchaToken && !recaptchaLoaded) {
      // console.log('CAPTCHA required but not loaded yet - waiting for script to load');
    }
  }, [captchaRequired, captchaToken, recaptchaLoaded]);

  const checkThrottleStatus = useCallback(async () => {
    // Don't check if we're in the last minute of throttling to prevent flashing
    if (throttled && throttleTimeRemaining > 0 && throttleTimeRemaining < 60) {
      return;
    }
    
    try {
      const response = await api.request(`/agent-call/throttle-status?agentId=${agentId}`, {
        method: 'GET'
      });
      
      // Create a stable update with both states together
      if (response.throttled) {
        // Only update if we're not already throttled or if the time is significantly different
        if (!throttled || Math.abs(response.timeRemaining - throttleTimeRemaining) > 60) {
          setThrottled(true);
          setThrottleTimeRemaining(Math.max(1, response.timeRemaining)); // Always at least 1 second
        }
      } else if (throttled && throttleTimeRemaining > 60) { 
        // Only reset if we're more than a minute away to prevent conflicts with countdown
        setThrottled(false);
        setThrottleTimeRemaining(0);
      }
    } catch (error) {
      // console.error("Error checking throttle status:", error);
      // Don't change state on error to avoid flashing
    }
  }, [agentId, throttled, throttleTimeRemaining]);

  // Countdown for throttled calls with debounced state updates
  useEffect(() => {
    let interval;
    let stateUpdateTimeout;
    
    if (throttled && throttleTimeRemaining > 0) {
      interval = setInterval(() => {
        setThrottleTimeRemaining(prev => {
          const newValue = prev - 1;
          // Only update throttled state when we're truly at zero
          if (newValue <= 0) {
            // Clear any pending state updates
            if (stateUpdateTimeout) {
              clearTimeout(stateUpdateTimeout);
            }
            
            // Schedule the throttled state change after a delay to prevent race conditions
            stateUpdateTimeout = setTimeout(() => {
              setThrottled(false);
            }, 500); // longer delay to ensure smooth transition
            
            return 0;
          }
          return newValue;
        });
      }, 1000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
      if (stateUpdateTimeout) clearTimeout(stateUpdateTimeout);
    };
  }, [throttled]);

  // Add a memoized value for display to prevent re-renders and flickering
  const minutesRemaining = useMemo(() => {
    if (!throttled || throttleTimeRemaining <= 0) return 0;
    // Always show at least "1 min" until the very end
    return Math.max(1, Math.ceil(throttleTimeRemaining / 60));
  }, [throttled, throttleTimeRemaining]);
  
  // Separate rendering logic from state update logic
  const isThrottledForDisplay = throttled && throttleTimeRemaining > 0;

  // Check for throttling on component mount and periodically
  useEffect(() => {
    // Initial check only when component mounts
    if (!throttled) {
      checkThrottleStatus();
    }

    // Set up periodic checking at a longer interval to prevent conflicts
    const periodicCheck = setInterval(() => {
      checkThrottleStatus();
    }, 120000); // Check every 2 minutes to reduce potential conflicts

    return () => {
      clearInterval(periodicCheck);
    };
  }, [checkThrottleStatus]);
  
  // Add a function to reset the captcha if needed
  const resetCaptcha = useCallback(() => {
    if (recaptchaRef.current) {
      try {
        recaptchaRef.current.reset();
        setCaptchaToken(null);
      } catch (error) {
        // console.error('Error resetting reCAPTCHA:', error);
      }
    }
  }, [recaptchaRef]);

  // Simplified function to set captcha required state
  const requireCaptcha = useCallback((required) => {
    // console.log(`Setting captchaRequired to: ${required}`);
    setCaptchaRequired(required);
    if (!required) {
      // When setting to false, also reset the token
      setCaptchaToken(null);
      resetCaptcha();
    }
  }, [resetCaptcha]);

  // Store initiateCallWithToken implementation in useRef to avoid circular dependencies
  functionRefs.current.initiateCallWithToken = async (verifiedToken) => {
    try {
      // console.log('Initiating call with verified token:', !!verifiedToken);
      
      // Set loading state
      setIsLoading(true);
      
      // Get authorization token from backend - use different method based on authentication state
      let response;
      if (isAuthenticated) {
        // Use authenticated endpoint for app users
        response = await api.authorizeWebAgentCall(agentId);
      } else {
        // Use public endpoint with captcha verification for public users
        response = await api.authorizePublicAgent(agentId, verifiedToken);
      }
      
      // Validate token from backend
      if (!response.token) {
        // console.error('No token in response', response);
        toast.error('Server did not return a valid token');
        setIsLoading(false);
        return;
      }
      
      // console.log('Received valid token from backend:', response.token.substring(0, 10) + '...');
      
      // We don't need to check requireCaptchaNextTime anymore
      // Let each API call determine the CAPTCHA requirement
      
      // console.log('Initializing VoiceCallWebClient with token');
      
      // Get the backend token
      const backendToken = response.token;
      
      try {
        // Check if VoiceCallWebClient is actually a function before trying to use it
        if (typeof VoiceCallWebClient !== 'function') {
          toast.error('Call service not available');
          setIsLoading(false);
          return;
        }
        
        // Ensure microphone permission is granted before initializing
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          
          // Release the stream immediately, we just needed permission
          stream.getTracks().forEach(track => track.stop());
        } catch (micError) {
          toast.error('Microphone access is required for the call. Please allow microphone access and try again.');
          setIsLoading(false);
          return;
        }
        
        try {
          // Set up console filter to suppress ScriptProcessorNode deprecation warning
          // This warning comes from the voice SDK which we can't modify directly
          if (!window._audioDeprecationWarningFiltered) {
            const originalConsoleWarn = console.warn;
            console.warn = function() {
              if (arguments[0] && 
                  typeof arguments[0] === 'string' && 
                  arguments[0].includes('ScriptProcessorNode is deprecated')) {
                // Ignore ScriptProcessorNode deprecation warnings
                return;
              }
              originalConsoleWarn.apply(console, arguments);
            };
            window._audioDeprecationWarningFiltered = true;
          }
          
          // Initialize the client
          const webClient = new VoiceCallWebClient(
            agentId,
            backendToken
          );
                    
          // Keep track of heartbeat to detect connection issues
          let heartbeatInterval;
          let lastHeartbeat = Date.now();
          let hasActiveConnection = false;
          
          // Register all possible event handlers to capture any potential errors
          // This is a more comprehensive approach to handle different SDK versions
          const eventHandlers = {
            'conversationStarted': (data) => {
              // console.log('VoiceCallWebClient conversationStarted event fired:', data);
              setIsCallActive(true);
              setIsLoading(false);
              hasActiveConnection = true;
              lastHeartbeat = Date.now();
              if (onCallStart) onCallStart();
              // toast.success('Call is now active!');
            },
            'conversationEnded': (data) => {
              // console.log('VoiceCallWebClient conversationEnded event fired:', data);
              clearInterval(heartbeatInterval);
              setIsCallActive(false);
              setClient(null);
              hasActiveConnection = false;
              if (onCallEnd) onCallEnd();
              
              // Reset captcha states for next server check
              setCaptchaToken(null);
              requireCaptcha(false); // Reset since we'll check with server on next call attempt
              resetCaptcha();
              
              // Use toast() or toast.success instead of toast.info
              toast('Call has ended');
            },
            'error': (error) => {
              // console.error('VoiceCallWebClient error event fired:', error);
              clearInterval(heartbeatInterval);
              hasActiveConnection = false;
              
              // More comprehensive error handling
              let errorMessage = 'An error occurred with the call';
              
              if (error) {
                // console.log('Error details:', {
                //   name: error.name,
                //   message: error.message,
                //   code: error.code,
                //   stack: error.stack
                // });
                
                // Check for different types of errors
                if (error.message?.includes('Timeout') || error.name === 'TimeoutError' || error.message?.includes('timeout')) {
                  errorMessage = 'Connection timeout. Please check your internet connection and try again.';
                } else if (error.name === 'NotAllowedError' || error.message?.includes('Permission')) {
                  errorMessage = 'Microphone access denied. Please allow microphone access and try again.';
                } else if (error.message?.includes('getUserMedia')) {
                  errorMessage = 'Cannot access your microphone. Please check your browser permissions.';
                } else if (error.message) {
                  errorMessage = error.message;
                }
              }
              
              // Always reset state on errors
              setIsCallActive(false);
              setIsLoading(false);
              setClient(null);
              
              // Reset captcha states for next server check
              setCaptchaToken(null);
              requireCaptcha(false); // Reset since we'll check with server on next call attempt
              resetCaptcha();
              
              toast.error(errorMessage);
              
              if (onError) onError(error || new Error(errorMessage));
            },
            // ... rest of event handlers ...
          };
          
          // Register all event handlers
          Object.entries(eventHandlers).forEach(([event, handler]) => {
            try {
              webClient.on(event, handler);
            } catch (e) {
              // console.warn(`Failed to register '${event}' handler:`, e);
            }
          });
          
          // Store client for future reference
          setClient(webClient);
          
          // ... rest of implementation ...
          
          // Start the conversation
          try {
            const options = {
              sampleRate: 44100,
              audio: {
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true
              },
              // Add options to handle the AudioContext deprecation warnings
              // These options may be passed to the underlying AudioContext by the SDK
              audioContextOptions: {
                // This tells browsers we're aware of the deprecation but need to use it for compatibility
                latencyHint: 'interactive',
                // Some browsers allow setting a sampleRate to avoid resampling
                sampleRate: 44100
              },
              timeout: 45000, // 45 seconds
            };
            
            // console.log('Calling initConversation with options:', options);
            await webClient.initConversation(options);
            
            // console.log('Call successfully started');
            setIsCallActive(true);
            setIsLoading(false);
            toast.success('Call is now active! Speak into your microphone.');
            
            if (onCallStart) onCallStart();
          } catch (convError) {
            // ... error handling for conversation initialization ...
            toast.error(convError?.message || 'Failed to start conversation');
            setIsCallActive(false);
            setIsLoading(false);
            setClient(null);
          }
        } catch (initError) {
          // Error creating VoiceCallWebClient
          throw initError;
        }
      } catch (error) {
        // console.error('Error initializing VoiceCallWebClient:', error);
        setIsLoading(false);
        setIsCallActive(false);
        toast.error('Failed to initialize call: ' + (error.message || 'Unknown error'));
      }
    } catch (error) {
      // console.error('Error during call initiation:', error);
      setIsLoading(false);
      
      // Inspect the error response data
      const errorData = error.response?.data || {};
      // console.log('Error response data:', errorData);
      
      if (error.response?.status === 429) {
        // Rate limited
        // console.log('Rate limited by server');
        setThrottled(true);
        setThrottleTimeRemaining(error.response.data?.timeRemaining || 300);
        toast.error('Too many calls. Please try again later.');
      } else if (error.response?.status === 403 || error.response?.status === 401) {
        // Handle both 403 and 401 status codes for captcha requirements
        
        // Check multiple possible response formats for captcha requirement
        const captchaRequired = 
          error.response.data?.requireCaptcha === true || 
          error.response.data?.message?.includes('Captcha verification required') ||
          error.response.data?.error?.includes('captcha') ||
          error.response.data?.status === 'captcha_required';
        
        if (captchaRequired) {
          // console.log('Server requires captcha verification - response:', error.response.data);
          requireCaptcha(true);
          toast('Please complete the verification below');
        } else {
          toast.error('This agent is not available for public use');
        }
      } else {
        toast.error(error.message || 'Failed to start call');
      }
      
      // Reset captcha token but not the requirement flag if not captcha required
      if (!error.response?.data?.requireCaptcha) {
        resetCaptcha();
      }
    }
  };

  // When API calls fail, use this instead of directly manipulating grecaptcha 
  useEffect(() => {
    if (!captchaToken && window.grecaptcha && recaptchaRef.current) {
      try {
        resetCaptcha();
      } catch (error) {
        // console.error('Error resetting grecaptcha on token change:', error);
      }
    }
  }, [captchaToken, resetCaptcha]);

  // reCAPTCHA callbacks
  const onCaptchaLoad = useCallback(() => {
    // console.log('reCAPTCHA widget loaded callback');
  }, []);

  const onCaptchaError = useCallback(() => {
    // console.error('reCAPTCHA error occurred');
    toast.error('Verification failed to load. Please try again later.');
    setCaptchaRequired(false);
  }, []);

  const onCaptchaExpired = useCallback(() => {
    // console.log('reCAPTCHA expired');
    setCaptchaToken(null);
    toast.error('Verification expired. Please try again.');
  }, []);

  const handleCaptchaChange = useCallback((token) => {
    // console.log('Captcha token received:', !!token);
    
    if (!token) {
      // Token was reset or is invalid
      setCaptchaToken(null);
      return;
    }
    
    // console.log('Valid captcha token received, starting call process');
    
    // Set token and immediately start call process
    setCaptchaToken(token);
    
    // Show a toast notification
    toast.success('Verification complete! Starting call...');
    
    // Start call directly using the reference
    functionRefs.current.initiateCallWithToken(token);
  }, []);
  
  const startCall = useCallback(async () => {
    // console.log('startCall called with state:', { 
    //   isCallActive, 
    //   isLoading, 
    //   throttled, 
    //   captchaRequired, 
    //   captchaToken: !!captchaToken 
    // });
    
    if (isCallActive || isLoading || throttled) {
      // console.log('Call prevented: already active, loading, or throttled');
      return;
    }
    
    if (captchaRequired && !captchaToken) {
      // No need to set showCaptcha here, as it will be shown based on captchaRequired
      toast('Please complete the verification below');
      return;
    }
    
    // If we already have a captcha token, use it directly
    if (captchaToken) {
      // console.log('Using existing captcha token');
      functionRefs.current.initiateCallWithToken(captchaToken);
      return;
    }
    
    // If no captcha is required, get authorization token directly
    setIsLoading(true);
    
    try {
      // Get authorization token - use different method based on authentication state
      let response;
      if (isAuthenticated) {
        // Use authenticated endpoint for app users
        response = await api.authorizeWebAgentCall(agentId);
      } else {
        // Use public endpoint for public users
        response = await api.authorizePublicAgent(agentId);
      }
      
      // When using the public endpoint, check if captcha is required
      if (!isAuthenticated && response.requireCaptcha) {
        requireCaptcha(true);
        setIsLoading(false);
        toast('Please complete the verification below');
        return;
      }
      
      // Extract token
      if (!response.token) {
        // console.error('No token in response', response);
        setIsLoading(false);
        toast.error('Server did not return a valid token');
        return;
      }
      
      // Continue with call initialization using the token
      // console.log('Got token without captcha, continue with call setup');
      
      // We don't need to set captchaRequired based on requireCaptchaNextTime
      // Each new API call will determine the CAPTCHA requirement
      
      // Use function reference instead of direct call
      functionRefs.current.initiateCallWithToken(null);
      
    } catch (error) {
      // console.error('Authorization error:', error);
      setIsLoading(false);
      
      // Inspect the error response data
      const errorData = error.response?.data || {};
      // console.log('Error response data:', errorData);
      
      if (error.response?.status === 429) {
        // Rate limited
        // console.log('Rate limited by server');
        setThrottled(true);
        setThrottleTimeRemaining(error.response.data?.timeRemaining);
        toast.error('Too many calls. Please try again later.');
      } else if (error.response?.status === 403 || error.response?.status === 401) {
        // Handle both 403 and 401 status codes for captcha requirements
        
        // Check multiple possible response formats for captcha requirement
        const captchaRequired = 
          error.response.data?.requireCaptcha === true || 
          error.response.data?.message?.includes('Captcha verification required') ||
          error.response.data?.error?.includes('captcha') ||
          error.response.data?.status === 'captcha_required';
        
        if (captchaRequired) {
          // console.log('Server requires captcha verification - response:', error.response.data);
          requireCaptcha(true);
          toast('Please complete the verification below');
        } else {
          toast.error('This agent is not available for public use');
        }
      } else {
        toast.error(error.message || 'Failed to authorize call');
      }
    }
  }, [agentId, isCallActive, isLoading, throttled, captchaRequired, captchaToken, requireCaptcha, isAuthenticated]);

  const endCall = useCallback(() => {
    if (!isCallActive || !client) {
      // console.log('Cannot end call: no active call or client');
      return;
    }
    
    // console.log('Attempting to end call with client');
    
    try {
      // Use the stopConversation method as in the original implementation
      if (typeof client.stopConversation === 'function') {
        // console.log('Using stopConversation method');
        client.stopConversation();
      } else {
        // Fallbacks if stopConversation doesn't exist
        if (typeof client.endCall === 'function') {
          // console.log('Using endCall method');
          client.endCall();
        } else if (typeof client.endConversation === 'function') {
          // console.log('Using endConversation method');
          client.endConversation();
        } else if (typeof client.disconnect === 'function') {
          // console.log('Using disconnect method');
          client.disconnect();
        } else if (typeof client.stop === 'function') {
          // console.log('Using stop method');
          client.stop();
        } else {
          // console.warn('No method found to end call - simply resetting state');
        }
      }
      
      // Reset states regardless of method call
      setIsCallActive(false);
      setClient(null);
      setCaptchaToken(null);
      requireCaptcha(false); // Reset since we'll check with server on next call attempt
      resetCaptcha();
      
      // Use toast() or toast.success instead of toast.info
      // toast('Call ended');
      if (onCallEnd) onCallEnd();
      
    } catch (error) {
      // console.error('Error ending call:', error);
      toast.error('Error ending call. Please reload the page.');
      
      // Force reset states
      setIsCallActive(false);
      setClient(null);
      setCaptchaToken(null);
      requireCaptcha(false); // Reset since we'll check with server on next call attempt
      resetCaptcha();
    }
  }, [isCallActive, client, resetCaptcha, onCallEnd, requireCaptcha]);

  const handleTriggerClick = () => {
    if (isCallActive) {
      // console.log('Ending active call');
      endCall();
    } else if (!isLoading) {
      // Remove the conditional check for showing captcha here
      // as we want the server to fully control when captcha is shown
      // User should never need to click to show captcha
      if (captchaRequired && !captchaToken) {
        // CAPTCHA will already be shown due to useEffect
        toast('Please complete the verification below');
      } else {
        // console.log('Starting call from button click');
        startCall();
      }
    } else {
      // console.log('Button clicked while loading - ignoring');
      toast('Call is connecting, please wait...');
    }
  };

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (client) {
        // console.log('Component unmounting, cleaning up call');
        // Use a direct approach for cleanup to avoid state update issues during unmount
        try {
          if (typeof client.stopConversation === 'function') {
            client.stopConversation();
          } else if (typeof client.endCall === 'function') {
            client.endCall();
          } else if (typeof client.endConversation === 'function') {
            client.endConversation();
          } else if (typeof client.disconnect === 'function') {
            client.disconnect();
          } else if (typeof client.stop === 'function') {
            client.stop();
          }
          // Reset captchaRequired flag for next check with server
          requireCaptcha(false);
        } catch (error) {
          // console.warn('Error during cleanup:', error);
        }
      }
    };
  }, [client, requireCaptcha]);

  // Render UI components based on state
  if (isThrottledForDisplay) {
    return (
      <div className={`chat-widget-container ${className}`}>
        <div className={`chat-widget mx-auto bg-black/40 backdrop-blur-md rounded-full border border-white/10 shadow-lg hover:shadow-2xl transition-all duration-300 ${compact ? 'w-full max-w-full px-3 py-2' : 'w-[320px] px-4 py-2'}`}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className={`${compact ? 'w-9 h-9 text-sm' : 'w-10 h-10'} avatar rounded-full bg-gray-800 flex items-center justify-center text-white font-medium border border-white/10 flex-shrink-0`}>
                {avatar}
              </div>
              <div className="flex flex-col min-w-0 flex-1">
                <span className="agent-name text-white font-medium truncate">{name}</span>
                <span className="agent-status text-sm text-red-400 truncate">
                  Try again in {minutesRemaining} min
                </span>
              </div>
            </div>
            <button 
              className={`${compact ? 'w-9 h-9' : 'w-10 h-10'} rounded-full flex items-center justify-center transition-all duration-300 relative group bg-red-500/50 cursor-not-allowed opacity-50 flex-shrink-0`}
              disabled={true}
              aria-label="Call limit reached"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="24" 
                height="24" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                className="w-5 h-5 text-white relative z-10"
              >
                <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-3.33-2.67m-2.67-3.34a19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91" />
                <line x1="22" x2="2" y1="2" y2="22" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`chat-widget-container ${className} relative`}>
      {/* ReCAPTCHA overlay */}
      {captchaRequired && !captchaToken && recaptchaLoaded && (
        <div className="absolute top-full mt-4 left-1/2 transform -translate-x-1/2 bg-white p-3 rounded-lg shadow-lg z-20 border border-gray-200">
          {/* console.log('Rendering reCAPTCHA component - captchaRequired:', captchaRequired, 'captchaToken:', !!captchaToken, 'recaptchaLoaded:', recaptchaLoaded) */}
          <div className="text-center mb-2 text-sm font-medium text-gray-700">Please verify you're human</div>
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={RECAPTCHA_SITE_KEY}
            onChange={handleCaptchaChange}
            onExpired={onCaptchaExpired}
            onErrored={onCaptchaError}
            onLoad={onCaptchaLoad}
            size="compact"
          />
        </div>
      )}
      
      {/* Show loading indicator if reCAPTCHA is not yet loaded */}
      {captchaRequired && !captchaToken && !recaptchaLoaded && (
        <div className="absolute top-full mt-4 left-1/2 transform -translate-x-1/2 bg-white p-3 rounded-lg shadow-lg z-20 border border-gray-200">
          <div className="text-center text-sm font-medium text-gray-700">Loading verification...</div>
          <div className="flex justify-center mt-2">
            <LoadingSpinnerRing size="md" className="!h-6 !w-6" />
          </div>
        </div>
      )}
      
      <div className={`chat-widget mx-auto bg-black/40 backdrop-blur-md rounded-full border border-white/10 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] ${compact ? 'w-full max-w-full px-3 py-2' : 'w-[320px] px-4 py-2'}`}>
        {isCallActive && (
          <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg animate-pulse z-10">
            Call Active
          </div>
        )}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className={`${compact ? 'w-9 h-9 text-sm' : 'w-10 h-10'} avatar rounded-full bg-gray-800 flex items-center justify-center text-white font-medium border border-white/10 flex-shrink-0`}>
              {avatar}
            </div>
            <div className="flex flex-col min-w-0 flex-1">
              <span className="agent-name text-white font-medium truncate">{name}</span>
              <span className={`agent-status text-sm ${isCallActive ? 'text-green-400 font-medium' : 'text-white/72'} truncate flex items-center`}>
                {isLoading ? 'Connecting...' : isCallActive ? (
                  <>
                    <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-1.5 animate-pulse"></span>
                    Call Active
                  </>
                ) : captchaRequired && !captchaToken ? 'Complete verification below' : status}
              </span>
            </div>
          </div>
          <button 
            className={`${compact ? 'w-9 h-9' : 'w-10 h-10'} rounded-full flex items-center justify-center transition-all duration-300 relative group flex-shrink-0 ${
              isCallActive 
                ? 'bg-red-500 hover:bg-red-600 shadow-[0_0_15px_rgba(239,68,68,0.5)]' 
                : isLoading
                ? 'bg-orange-500 hover:bg-orange-600 cursor-wait shadow-[0_0_15px_rgba(249,115,22,0.5)]'
                : captchaRequired && !captchaToken 
                ? 'bg-yellow-500 hover:bg-yellow-600 shadow-[0_0_15px_rgba(234,179,8,0.5)] animate-pulse'
                : 'bg-green-500 hover:bg-green-600 animate-[glow_2s_ease-in-out_infinite] before:absolute before:inset-0 before:rounded-full before:shadow-[0_0_20px_8px_rgba(34,197,94,0.5)] before:animate-[glow_2s_ease-in-out_infinite] before:-z-10'
            }`}
            onClick={handleTriggerClick}
            disabled={isLoading}
            aria-label={
              isLoading 
                ? "Connecting call" 
                : isCallActive 
                ? "End voice call" 
                : captchaRequired && !captchaToken
                ? "Verification required"
                : "Start voice call"
            }
            tabIndex={0}
            style={{
              transform: 'scale(0.999846)'
            }}
          >
            {/* Pulsating glow rings */}
            {!isCallActive && !isLoading && !(captchaRequired && !captchaToken) && (
              <>
                <div className="absolute inset-0 rounded-full animate-[glow_2s_ease-in-out_infinite] opacity-75" 
                     style={{
                       boxShadow: '0 0 20px 8px rgba(34,197,94,0.4)',
                       animationDelay: '-1s'
                     }} />
                <div className="absolute inset-0 rounded-full animate-[glow_2s_ease-in-out_infinite] opacity-50"
                     style={{
                       boxShadow: '0 0 30px 12px rgba(34,197,94,0.3)',
                       animationDelay: '-0.5s'
                     }} />
              </>
            )}
            
            {/* Enhanced hover effect */}
            <div className={`absolute inset-0 rounded-full transition-opacity duration-300 ${
              isCallActive 
                ? 'bg-red-500/20' 
                : isLoading
                ? 'bg-orange-500/20'
                : captchaRequired && !captchaToken
                ? 'bg-yellow-500/20'
                : 'bg-green-500/20'
            } opacity-0 group-hover:opacity-100`} />

            <style jsx="true">{`
              @keyframes glow {
                0%, 100% { opacity: 0.3; }
                50% { opacity: 1; }
              }
            `}</style>
            
            {isLoading ? (
              <LoadingSpinnerRing tone="onDark" className="relative z-10 !h-5 !w-5" />
            ) : isCallActive ? (
              /* Phone off icon */
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="24" 
                height="24" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                className="w-5 h-5 text-white relative z-10"
              >
                <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-3.33-2.67m-2.67-3.34a19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91" />
                <line x1="22" x2="2" y1="2" y2="22" />
              </svg>
            ) : captchaRequired && !captchaToken ? (
              /* Shield/verification icon */
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="24" 
                height="24" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                className="w-5 h-5 text-white relative z-10"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            ) : (
              /* Phone icon */
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="24" 
                height="24" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-5 h-5 text-white relative z-10"
              >
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

ChatWidget.propTypes = {
  agentId: PropTypes.string.isRequired,
  name: PropTypes.string,
  avatar: PropTypes.node,
  status: PropTypes.string,
  onCallStart: PropTypes.func,
  onCallEnd: PropTypes.func,
  onError: PropTypes.func,
  isAuthenticated: PropTypes.bool,
  className: PropTypes.string,
  compact: PropTypes.bool
};

export default ChatWidget; 
